import React, { useState } from 'react'
import { Link } from '@tanstack/react-router'
import {
  Breadcrumb,
  BreadcrumbItem,
  BreadcrumbLink,
  BreadcrumbList,
  BreadcrumbPage,
  BreadcrumbSeparator,
} from './ui/breadcrumb'
import { useBreadcrumb } from '../contexts/BreadcrumbContext'
import type { BreadcrumbItem as BreadcrumbEntry } from '../contexts/BreadcrumbContext'
import { ArchivedMarker } from './ArchivedMarker'

function CrumbLabel({ item }: { item: BreadcrumbEntry }) {
  if (!item.archivedAt) return <>{item.title}</>
  return (
    <span className="inline-flex items-center gap-1.5">
      <span className="line-through">{item.title}</span>
      <ArchivedMarker archivedAt={item.archivedAt} testId="breadcrumb-archived-marker" />
    </span>
  )
}

const COLLAPSE_THRESHOLD = 4 // segments — Home, last, direct parent + 1 middle still fit

export function AppBreadcrumb() {
  const { breadcrumbs } = useBreadcrumb()
  const [expanded, setExpanded] = useState(false)

  if (breadcrumbs.length === 0) {
    return null
  }

  const total = breadcrumbs.length
  const shouldCollapse = !expanded && total > COLLAPSE_THRESHOLD
  // Spec 01 §4 — always keep first (Home), last (current), direct parent of current.
  // If collapsed, replace the middle with a single … pill that expands on click.
  const visibleIndices: number[] = []
  if (shouldCollapse) {
    visibleIndices.push(0)
    if (total >= 3) visibleIndices.push(total - 2) // direct parent
    visibleIndices.push(total - 1) // current
  } else {
    for (let i = 0; i < total; i++) visibleIndices.push(i)
  }

  return (
    <Breadcrumb>
      <BreadcrumbList>
        {visibleIndices.map((idx, i) => {
          const item = breadcrumbs[idx]
          const prevIdx = i === 0 ? -1 : visibleIndices[i - 1]
          const isFirst = i === 0
          const collapsed = shouldCollapse && prevIdx >= 0 && idx - prevIdx > 1
          return (
            <React.Fragment key={idx}>
              {!isFirst && <BreadcrumbSeparator />}
              {collapsed && (
                <>
                  <BreadcrumbItem>
                    <button
                      type="button"
                      onClick={() => setExpanded(true)}
                      className="px-1.5 py-0.5 rounded-md text-muted-foreground hover:bg-muted hover:text-foreground transition-colors"
                      data-testid="breadcrumb-expand"
                      title={breadcrumbs.slice(prevIdx + 1, idx).map((b) => b.title).join(' / ')}
                    >
                      …
                    </button>
                  </BreadcrumbItem>
                  <BreadcrumbSeparator />
                </>
              )}
              <BreadcrumbItem>
                {item.path ? (
                  <BreadcrumbLink asChild>
                    <Link to={item.path} title={item.title}><CrumbLabel item={item} /></Link>
                  </BreadcrumbLink>
                ) : (
                  <BreadcrumbPage title={item.title}><CrumbLabel item={item} /></BreadcrumbPage>
                )}
              </BreadcrumbItem>
            </React.Fragment>
          )
        })}
      </BreadcrumbList>
    </Breadcrumb>
  )
}
